import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { AIAgent, PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { AgentModeForm, AgentModeStartView } from './agent-mode-form';
import { ChatbotAgentModeEmptyState } from './agent-mode/chatbot-agent-mode-empty-state';
import {
  $aiHelperAgent,
  $chatbotAgentModeMainTabState,
  AgentModeMainTabState,
  abortAgentMessageEvent,
  createAIHelperAgentThreadFx,
  createAgentMessageEvent,
  setChatbotAgentModeMainTabStateEvent,
} from './agent-store';
import { ChatbotChat } from './chatbot-chat';
import { ChatbotContext } from './chatbot-context';
import { ChatbotInput } from './chatbot-input';
import {
  $activeThreadId,
  $aiThreads,
  $editMessage,
  $writingMessage,
  onMessageResolvedEvent,
  setActiveThreadIdEvent,
  setEditMessageIdEvent,
  updateThreadEvent,
} from './store';
import { AgentInput, ChatBotAttachedFile } from './types';
import { useAgentGenerateResponse } from './use-agent-generate-response';
import { useChatbotFileUpload } from './use-chatbot-file-upload';
import { isTextInputNode } from './util';

type ChatbotAgentModeMainTabProps = {
  agent?: AIAgent;
  agentLoaded?: boolean;
  startWithExistingThread?: boolean;
  noInputLineMode?: boolean;
  smoothScroll?: boolean;
  autoRunIfPossible?: boolean;
};

export const ChatbotAgentModeMainTab: FC<ChatbotAgentModeMainTabProps> = (props: ChatbotAgentModeMainTabProps) => {
  const [aiHelperAgent, chatbotAgentModeMainTabState, activeThreadId, aiThreads, writingMessage, editMessage] = useUnit(
    [$aiHelperAgent, $chatbotAgentModeMainTabState, $activeThreadId, $aiThreads, $writingMessage, $editMessage]
  );

  const [
    createAgentMessage,
    onMessageResolved,
    abortAgentMessage,
    setChatbotAgentModeMainTabState,
    setActiveThreadId,
    updateThread,
    setEditMessageId,
  ] = useUnit([
    createAgentMessageEvent,
    onMessageResolvedEvent,
    abortAgentMessageEvent,
    setChatbotAgentModeMainTabStateEvent,
    setActiveThreadIdEvent,
    updateThreadEvent,
    setEditMessageIdEvent,
  ]);

  const { agent, agentLoaded, startWithExistingThread, smoothScroll = false, autoRunIfPossible = false } = props;
  const aiContext = useContext(ChatbotContext);

  const [creatingThreadInProgress, setCreatingThreadInProgress] = useState(false);
  const [formDisappearing, setFormDisappearing] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [emptyStateFading, setEmptyStateFading] = useState(false);
  const createdThreadWithFirstMessageRef = useRef<boolean>(false);
  const formDisappearingDuration = 500;

  const chatbotInputRef = useRef<HTMLTextAreaElement | null>(null);

  const t = useTranslations('features');

  let inputPlaceholderText = activeThreadId ? t('ai-helper.tell_what_to_do_next') : t('ai-helper.start_a_chat2');
  if (aiContext.portalId) {
    inputPlaceholderText = t('ai-helper.ask_a_question');
    if (!aiContext.isVisitor) {
      inputPlaceholderText += ' ' + t('ai-helper.use_to_add_context');
    }
  }

  const aiIsWritingMessageInCurrentThread = useMemo(() => {
    return (
      Boolean(aiThreads.tmpMessages?.length) ||
      (activeThreadId !== null && Boolean(aiThreads.threads[activeThreadId]?.isWriting))
    );
  }, [activeThreadId, aiThreads.threads, aiThreads.tmpMessages?.length]);

  useEffect(() => {
    if (!aiIsWritingMessageInCurrentThread) {
      // Need to focus on the assistant input box on assistant popup open and when AI finishes writing.
      // However, we don't want to focus on the input box if the user is typing in another editable element.
      if (!isTextInputNode(document.activeElement as HTMLElement)) {
        if (chatbotInputRef.current) {
          chatbotInputRef.current.focus();
        }
      }
    }
  }, [aiIsWritingMessageInCurrentThread]);

  const inputsExist = useMemo(() => {
    if (!agentLoaded) {
      return true;
    }
    return Boolean(agent && agent?.inputs.length);
  }, [agent, agentLoaded]);

  const onGenerateResponse = useAgentGenerateResponse({
    agent,
    setCreatingThreadInProgress,
  });

  const autoRun = useMemo(() => {
    return agentLoaded && !inputsExist && autoRunIfPossible && agent?.webpageDetails && !startWithExistingThread;
  }, [agentLoaded, inputsExist, autoRunIfPossible, agent?.webpageDetails, startWithExistingThread]);

  // Effect for running agents automatically if no start screen and inputs
  const autoRunTriggered = useRef(false);
  useEffect(() => {
    if (autoRun && !autoRunTriggered.current) {
      autoRunTriggered.current = true;
      onGenerateResponse();
    }
  }, [onGenerateResponse, autoRun]);

  const { getRootProps, getInputProps, open, files, removeFile, resetFiles, isDragActive, addFiles } =
    useChatbotFileUpload();

  const onRemoveFile = useCallback(
    (file: ChatBotAttachedFile) => {
      removeFile(file);
    },
    [removeFile]
  );

  const isFilesUploading = useMemo(() => {
    return files.some((file) => file.uploadState === 'uploading');
  }, [files]);

  const onSendMessage = useCallback(
    async (text: string) => {
      if (isFilesUploading) {
        return;
      }

      let createdThreadId: string | null = null;
      if (!activeThreadId) {
        createdThreadWithFirstMessageRef.current = true;
        setEmptyStateFading(true);
        // create agent thread first
        await createAIHelperAgentThreadFx({
          orgId: aiContext.orgId as string,
          agentId: agent?.globalId as string,
          // if we are creating a new thread here then input is empty
          input: {},
          workspaceId: aiContext.workspaceId as string,
        }).then(({ id, input }) => {
          createdThreadId = id;
          setActiveThreadId(id);
          updateThread({
            id,
            thread: { input },
          });
        });
      }

      const abortController = new AbortController();

      if (!activeThreadId && !createdThreadId) {
        return console.error('Sending agent message without active thread id');
      }

      const filesToCreateMessage = files.filter((file) => Boolean(file.tempStoredFileName));

      resetFiles();

      const usingThreadId = activeThreadId ? activeThreadId : createdThreadId;
      if (!usingThreadId) {
        return console.error('Sending agent message without active thread id');
      }

      createAgentMessage({
        apiHost: aiContext.apiHostCreateMessage,
        sessionId: aiContext.sessionId,
        orgId: aiContext.orgId as string,
        threadId: usingThreadId,
        workspaceId: aiContext.workspaceId as string,
        text,
        abortController,
        onMessageChunkResolved: onMessageResolved,
        files: filesToCreateMessage,
        webPageDetails: aiContext.webPageDetails,
        rewriteId: editMessage?.id,
      });
    },
    [
      isFilesUploading,
      activeThreadId,
      files,
      resetFiles,
      editMessage,
      createAgentMessage,
      aiContext.apiHostCreateMessage,
      aiContext.sessionId,
      aiContext.orgId,
      aiContext.workspaceId,
      aiContext.webPageDetails,
      onMessageResolved,
      agent?.globalId,
      setActiveThreadId,
      updateThread,
    ]
  );

  const onEndEditMessage = () => {
    if (!activeThreadId) {
      return;
    }
    setEditMessageId({ threadId: activeThreadId, messageId: null });
  };

  useEffect(() => {
    if (agentLoaded) {
      if (!agent) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.AGENT_NOT_FOUND);
        return;
      }
      if (aiHelperAgent.state === 'error') {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.ERROR);
        return;
      }
      if (activeThreadId) {
        if (chatbotAgentModeMainTabState !== AgentModeMainTabState.CHAT) {
          // run form disappearance animation only when form was submitted in this component
          if (formSubmitted) {
            setFormDisappearing(true);
            setTimeout(() => {
              setChatbotAgentModeMainTabState(AgentModeMainTabState.CHAT);
              setFormDisappearing(false);
            }, formDisappearingDuration);
          } else {
            setChatbotAgentModeMainTabState(AgentModeMainTabState.CHAT);
          }
        }
        return;
      }
      if (autoRun) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
        return;
      } else if (!inputsExist) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.CHAT);
        return;
      } else if (inputsExist) {
        setChatbotAgentModeMainTabState(AgentModeMainTabState.INPUT);
        return;
      }
      setChatbotAgentModeMainTabState(AgentModeMainTabState.CHAT);
    } else {
      setChatbotAgentModeMainTabState(AgentModeMainTabState.LOADING);
    }
  }, [
    inputsExist,
    onGenerateResponse,
    agentLoaded,
    activeThreadId,
    aiHelperAgent.state,
    agent,
    setChatbotAgentModeMainTabState,
    chatbotAgentModeMainTabState,
    formSubmitted,
    autoRun,
  ]);

  useEffect(() => {
    if (!activeThreadId) {
      setEmptyStateFading(false);
    }
  }, [activeThreadId]);

  const formLayout = useCallback(
    ({ children }: { children: React.ReactNode }) => (
      <div
        className={classNames('flex mt-auto pb-6', {
          'px-5': !aiContext.pageMode,
          'px-[44px]': aiContext.pageMode,
        })}
      >
        {children}
      </div>
    ),
    [aiContext.pageMode]
  );

  const onRunTaskClick = useCallback(() => {
    if (inputsExist) {
      setChatbotAgentModeMainTabState(AgentModeMainTabState.INPUT);
    } else {
      onGenerateResponse();
    }
  }, [inputsExist, onGenerateResponse, setChatbotAgentModeMainTabState]);

  const onFormSubmit = useCallback(
    (input: AgentInput) => {
      setFormSubmitted(true);
      onGenerateResponse(input);
    },
    [onGenerateResponse]
  );

  const onAbortMessage = useCallback(
    (messageId: string, text: string) => {
      if (!aiContext.orgId) {
        return console.error('No orgId provided, fail to cancel agent message');
      }
      abortAgentMessage({
        sessionId: aiContext.sessionId,
        orgId: aiContext.orgId,
        messageId,
        text,
      });
    },
    [abortAgentMessage, aiContext.sessionId, aiContext.orgId]
  );

  const onPaste = useCallback(
    (event: React.ClipboardEvent) => {
      if (event?.clipboardData?.files.length) {
        addFiles(Array.from(event?.clipboardData?.files));
      }
    },
    [addFiles]
  );

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.LOADING) {
    return (
      <div className="flex flex-col h-full pt-5">
        <div className="p-4 flex flex-col mb-6 mt-auto w-full max-w-2xl mx-auto">
          <ChatbotAgentModeMainTabSkeleton barsCount={aiContext.pageMode ? 3 : undefined} />
        </div>
      </div>
    );
  }

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.ERROR) {
    return <div>Fail to load agent.</div>;
  }

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.AGENT_NOT_FOUND) {
    return <div className="text-center mt-5">Agent not found</div>;
  }

  let content;

  if (chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT) {
    if (!activeThreadId) {
      content = <ChatbotAgentModeEmptyState agent={agent} fading={emptyStateFading} />;
    } else {
      content = (
        <ChatbotChat
          disableLoading={true}
          threadId={activeThreadId}
          onSendMessage={onSendMessage}
          themeColors={aiContext.themeColors as ThemeColorSchemas}
          themeStyle={aiContext.themeStyle as PortalStyleConfig}
          aiName={'Agent'}
          aiCustomLogoUrl={null}
          isWriting={aiIsWritingMessageInCurrentThread}
          onAddMessageToPage={() => {
            /* void */
          }}
          onFeedback={() => {
            /* void */
          }}
          allowCreatePage={false}
          isAgent={true}
          enableAutoScroll={!smoothScroll}
          initialScrollToAssistantWriting={smoothScroll && !createdThreadWithFirstMessageRef.current}
          scrollToUserMessage={smoothScroll}
          animateIn={true}
        />
      );
    }
  }

  if (agent && chatbotAgentModeMainTabState === AgentModeMainTabState.START_VIEW) {
    content = formLayout({ children: <AgentModeStartView agent={agent} onRunClick={onRunTaskClick} /> });
  }

  if (agent && chatbotAgentModeMainTabState === AgentModeMainTabState.INPUT) {
    content = formLayout({
      children: (
        <div
          className={classNames('h-full w-full', {
            'chatbot-agent-form-disappearing': formDisappearing,
          })}
          style={formDisappearing ? { transitionDuration: formDisappearingDuration + 'ms' } : {}}
        >
          <AgentModeForm agent={agent} onSubmit={onFormSubmit} creatingThreadInProgress={creatingThreadInProgress} />
        </div>
      ),
    });
  }

  return !agent ? null : (
    <div
      className="flex flex-col h-full"
      {...(chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT ? getRootProps() : {})}
    >
      {chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT && <input {...getInputProps()} />}
      {isDragActive && (
        <div className="absolute inset-0 z-50 backdrop-blur-sm bg-white/70 flex items-center justify-center">
          <div className="text-center flex flex-col items-center">
            <div className="bg-neutral-800 rounded-lg w-[65px] h-[65px] flex justify-center items-center mb-1.5">
              <Icon icon={IconsMap.ADD_PAGE} className="w-8 h-8 text-white" />
            </div>
            <p className="text-sm font-bold">Drop file here to add to conversation</p>
          </div>
        </div>
      )}
      {content}
      {!props.noInputLineMode && chatbotAgentModeMainTabState === AgentModeMainTabState.CHAT && (
        <div onPaste={onPaste}>
          <ChatbotInput
            sendDisabled={isFilesUploading}
            chosenFiles={files}
            fileUpload={true}
            chooseFile={open}
            inputRef={chatbotInputRef}
            themeColors={aiContext.themeColors as ThemeColorSchemas}
            themeStyle={aiContext.themeStyle as PortalStyleConfig}
            onSend={onSendMessage}
            onEndEditMessage={onEndEditMessage}
            isWriting={aiIsWritingMessageInCurrentThread}
            isCreatingPage={
              aiIsWritingMessageInCurrentThread &&
              activeThreadId &&
              aiThreads.threads[activeThreadId].messages &&
              writingMessage?.system
                ? true
                : false
            }
            useWeb={false}
            onSwitchUseWeb={() => {
              /* void */
            }}
            onAbortMessage={onAbortMessage}
            placeholder={inputPlaceholderText}
            onRemoveFile={onRemoveFile}
          />
        </div>
      )}
    </div>
  );
};

const ChatbotAgentModeMainTabSkeleton = (props: { barsCount?: number }) => {
  const baseColor = 'rgb(245, 245, 245)';
  const highlightColor = '#fff';

  const { barsCount = 2 } = props;

  return (
    <div className="flex flex-col gap-6 w-full">
      {Array.from({ length: barsCount }).map((_, index) => (
        <Skeleton baseColor={baseColor} highlightColor={highlightColor} height={144} key={index} />
      ))}
      <div className="flex justify-center">
        <Skeleton baseColor={baseColor} highlightColor={highlightColor} height={39} width={137} />
      </div>
    </div>
  );
};
